<script setup lang="ts">
import { computed } from 'vue'
import { RemoteFile } from '@/core/resources/RemoteFile'
import { fileIcons, formatBytes } from '@/utils/fileUtil'

const props = defineProps<{
  file: RemoteFile
  selectable: boolean
}>()

const selected = defineModel<boolean>('selected', { type: Boolean })

const isDir = computed(() => props.file.type === 'dir')

const emit = defineEmits({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  changePath: (path: string) => true,
  download: () => true,
  delete: () => true
})

const handleClick = () => {
  if (isDir.value) {
    emit('changePath', props.file.path)
  } else {
    selected.value = !selected.value
  }
}
</script>

<template>
  <div class="file-list-item" @click="handleClick">
    <div class="file-list-item__left">
      <input type="checkbox" v-model="selected" v-if="props.selectable && !isDir" />

      <span class="material-symbols-outlined" :class="{ hidden: file.basename.startsWith('.') }">
        {{ isDir ? 'folder' : fileIcons[file.extension.slice(1)] || fileIcons.other }}
      </span>

      <div class="file-list-item__details">
        <div>{{ file.basename }}</div>
        <div v-if="!isDir" class="additional-info">
          <span>{{ formatBytes(file.size) }}</span>
          <span> - </span>
          <span>{{ file.date.toLocaleString() }}</span>
        </div>
      </div>
    </div>

    <div class="file-list-item__right">
      <span v-if="!isDir" class="material-symbols-outlined action download" @click.stop="$emit('download')"> download </span>
      <span class="material-symbols-outlined action delete" @click.stop="$emit('delete')">delete</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.file-list-item {
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0.2rem;
  justify-content: space-between;

  transition: background-color 0.2s;

  user-select: none;

  &:hover {
    background-color: var(--theme-panel-secondary);
  }

  &:active {
    background-color: var(--theme-panel-tertiary);
  }
}

.additional-info {
  color: #666;
}

input[type='checkbox'] {
  transform: scale(1.2);
}

.file-list-item__left {
  display: flex;
  align-items: center;
  overflow: hidden;
}

.file-list-item__right {
  display: flex;
  align-items: center;
}

.material-symbols-outlined {
  font-size: 1.5rem;
  margin-right: 0.5rem;
  margin-left: 0.5rem;
  color: #555;

  &.hidden {
    color: var(--theme-panel-tertiary);
  }
}

.action {
  cursor: pointer;

  &:hover {
    color: var(--theme-color);
  }
}
</style>
